import { ChevronRight } from 'lucide-react';
import { Children, isValidElement } from 'react';
import type { ComponentProps, ReactNode } from 'react';
import { cn } from '../lib/cn.ts';
import { formatInt } from '../lib/format.ts';
import { LiveNumber } from './live/LiveNumber.tsx';
import { Skeleton } from './Skeleton.tsx';
import { IconTile } from './IconTile.tsx';
import { Tooltip } from './Tooltip.tsx';
import type { IconTileTone } from './IconTile.tsx';

export interface StatTileProps {
  /** Sentence case, no colon: "Needs you". Also the tile's `data-metric`. */
  label: string;
  value: number;
  /** "of 3" after the value. */
  of?: number;
  /** A short unit after the value: "in flight". */
  unit?: string;
  /** One line under the value: what the number is made of ("2 blocked, 6 to answer"). */
  sub?: ReactNode;
  /** The full breakdown, as the sub line's tooltip. */
  hint?: string;
  /** What the number means, in one plain line: the label's tooltip (hover or focus). */
  help?: string;
  /** Amber value and a quiet amber edge: only for the one number a person must act on, and only when above 0. */
  attention?: boolean;
  /** Formats the value; default whole numbers. */
  format?: (n: number) => string;
  icon?: ReactNode;
  /** The icon tile's tint (default accent; attention for Needs you). */
  iconTone?: IconTileTone;
  /** Opens the list that holds exactly this number. */
  onOpen?: () => void;
  /** A small chart or meter beside the value (a Sparkline or a RingMeter). */
  trend?: ReactNode;
  /** A full-width figure under the value (a StateBar or a RunStrip). */
  visual?: ReactNode;
  loading?: boolean;
  /**
   * One small button for the tile's own next step ("Answer"), at the end of the label line (top
   * right), at every width. With
   * it the tile stays a door to its list (onOpen covers the cell) and the button sits on top.
   */
  action?: ReactNode;
  /** At zero the value reads dim, quiet text rather than a figure to look at. */
  quietAtZero?: boolean;
  /**
   * The strip's one brand figure (colour.md, the warm role): the value in the Prism headline
   * gradient, cool into a warm tail. One per strip, on a count that is not a status; attention
   * and a zero value win over it.
   */
  featured?: boolean;
  className?: string;
}

/**
 * One number as a cell of a StatStrip (data-viz.md, "KPI tiles"): a small label, the live value
 * that rolls when it changes with its figure beside it (a bar, a sparkline, a ring), and one dim
 * line of what it is made of. The door to the list behind it is the whole cell. Every page's
 * numbers use this one pattern: one panel, cells set apart by thin lines and room.
 */
export function StatTile({ label, value, of, unit, sub, hint, help, attention, format = formatInt, icon, iconTone, onOpen, trend, visual, loading, action, quietAtZero, featured, className }: StatTileProps) {
  const figure = trend ?? visual;
  const wide = visual != null && trend == null;
  const labelLine = (
    // The label wraps rather than truncates: a tile never cuts its own name.
    <span className="flex min-w-0 items-center gap-2 text-caption text-fg-subtle max-sm:whitespace-nowrap">
      {icon && <IconTile icon={icon} tone={iconTone ?? (attention ? 'attention' : 'accent')} size="sm" />}
      {help ? (
        <Tooltip content={help}>
          <span className="min-w-0 cursor-help underline decoration-border-strong decoration-dotted underline-offset-4">{label}</span>
        </Tooltip>
      ) : (
        <span className="min-w-0">{label}</span>
      )}
      {action && !loading && (
        // The tile's own next step at the end of the label line, at every width; above the stretched door.
        // Below 640 px the label line takes the button's height, so it never sits over the line below.
        <span className="relative ml-auto shrink-0 sm:-my-1.5">{action}</span>
      )}
      {onOpen && !action && <ChevronRight className="ml-auto size-3.5 shrink-0 text-fg-faint opacity-0 transition duration-(--duration-fast) group-hover/tile:translate-x-0.5 group-hover/tile:opacity-100" aria-hidden />}
    </span>
  );
  const body = loading ? (
    <>
      {labelLine}
      <span className="flex flex-col gap-2 pt-1" role="status" aria-label={`Loading ${label}`}>
        <Skeleton className="h-8 w-16" />
        <Skeleton className="h-3 w-24" />
      </span>
    </>
  ) : (
    // Below 640 px a tile is one full-width row: the label and its line on the left, the value and
    // its figure on the right. From 640 px the wrappers dissolve (contents) into the stacked cell:
    // label, value with its figure, then the line.
    <span className="flex min-w-0 items-center gap-4 sm:contents">
      <span className="flex min-w-0 flex-1 flex-col gap-1 sm:contents">
        {labelLine}
        {sub != null && (
          <span className="line-clamp-2 min-w-0 text-caption text-fg-subtle sm:order-last sm:line-clamp-1" title={hint}>
            {sub}
          </span>
        )}
      </span>
      <span className="flex min-w-0 shrink-0 flex-row-reverse items-center gap-3 sm:flex-row sm:gap-4">
        <span className="flex shrink-0 items-baseline gap-1">
          <LiveNumber value={value} format={format} className={cn('font-display text-metric num-display', attention ? 'text-status-attention' : featured && value > 0 ? 'prism-headline' : quietAtZero && value === 0 ? 'text-fg-subtle' : 'text-fg')} />
          {of != null && <span className="text-ui text-fg-subtle num-display">of {format(of)}</span>}
          {unit && <span className="text-ui text-fg-subtle">{unit}</span>}
        </span>
        {figure != null && (
          // The figure stays inside the tile's padding: a trend never runs past the panel edge.
          <span className={cn('flex min-w-0 items-center', wide ? 'w-16 sm:w-auto sm:flex-1 [&>*]:w-full' : 'max-w-16 shrink-0 sm:ml-auto sm:max-w-full [&_svg]:h-auto [&_svg]:max-w-full')}>{figure}</span>
        )}
      </span>
    </span>
  );
  const frame = cn(
    'group/tile relative flex min-w-0 flex-col gap-2 sm:gap-1 bg-surface px-4 py-3 text-left sm:px-8 sm:py-4',
    onOpen && 'transition-colors duration-(--duration-fast) hover:bg-hover',
    className,
  );
  if (action && !loading) {
    // A stretched door under the content, the action above it: no button inside a button.
    return (
      <div className={frame} data-metric={label}>
        {onOpen && <button type="button" onClick={onOpen} aria-label={`Open ${label}`} className="absolute inset-0 rounded-card focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-focus" />}
        {body}
      </div>
    );
  }
  if (onOpen && !loading) {
    return (
      <button type="button" onClick={onOpen} className={frame} data-metric={label}>
        {body}
      </button>
    );
  }
  return (
    <div className={frame} data-metric={label}>
      {body}
    </div>
  );
}

export interface StatStripProps extends ComponentProps<'section'> {
  /** Accessible name: "Live counts". */
  label: string;
}

/**
 * The numbers of a page in one slim panel: cells side by side, set apart by thin inset lines
 * with room above and below, five across from 1024 px, two below it.
 */
export function StatStrip({ label, className, children, ...rest }: StatStripProps) {
  // A strip of StatTiles stacks one tile per row below 640 px (each tile lays itself out as a row
  // there), so no label or line is cut in a half-width cell. Other cells keep two columns.
  const rows = Children.toArray(children).some((c) => isValidElement(c) && c.type === StatTile);
  return (
    <section
      aria-label={label}
      className={cn(
        'inset-dividers grid overflow-hidden rounded-card border border-border bg-surface shadow-elevation-flat lg:grid-cols-5',
        rows ? 'grid-cols-1 max-sm:*:before:hidden sm:grid-cols-2' : 'grid-cols-2',
        className,
      )}
      {...rest}
    >
      {children}
    </section>
  );
}

export interface BannerProps {
  tone?: 'neutral' | 'attention' | 'danger' | 'success';
  icon?: ReactNode;
  /** What happened, one short sentence. */
  title: ReactNode;
  /** Why, and what to do next. */
  children?: ReactNode;
  /** Buttons: verb plus object. */
  actions?: ReactNode;
  className?: string;
}

const BANNER_TONE = {
  neutral: { box: 'bg-surface', icon: 'text-fg-subtle', stripe: '' },
  attention: { box: 'bg-status-attention-soft/30', icon: 'text-status-attention', stripe: 'bg-status-attention-fill' },
  danger: { box: 'bg-status-danger-soft/30', icon: 'text-status-danger', stripe: 'bg-status-danger-fill' },
  success: { box: 'bg-status-success-soft/30', icon: 'text-status-success', stripe: 'bg-status-success-fill' },
} as const;

/**
 * An inline banner inside the page (never a toast for an error): what happened, why, what to
 * do next, and its actions. Sized to its importance: a slim bar on the page's surface, the status
 * only in a thin stripe at its start, its icon and a whisper of tint, never a coloured sheet. The
 * title and the why read as one line where they fit.
 */
export function Banner({ tone = 'neutral', icon, title, children, actions, className }: BannerProps) {
  const t = BANNER_TONE[tone];
  return (
    <div
      role={tone === 'danger' ? 'alert' : 'status'}
      className={cn('relative flex min-w-0 flex-wrap items-center gap-x-3 gap-y-2 overflow-hidden rounded-card border border-border-subtle px-4 py-2', t.box, className)}
    >
      {t.stripe && <span aria-hidden className={cn('absolute inset-y-0 left-0 w-0.5', t.stripe)} />}
      {icon && <span className={cn('flex shrink-0 self-start pt-0.5 [&_svg]:size-4', t.icon)} aria-hidden>{icon}</span>}
      <div className="flex min-w-0 flex-1 basis-40 flex-wrap items-baseline gap-x-2 gap-y-0.5">
        <p className="text-ui font-medium text-fg">{title}</p>
        {children != null && <div className="text-caption text-fg-subtle">{children}</div>}
      </div>
      {actions != null && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}
